import * as React from 'react';

import { FluidHoverHighlight } from '@/components/fluid-hover-highlight';

import {
    type UseFluidHoverReturn,
    useFluidHover,
    useRegisterFluidHoverItem
} from '@/hooks/use-fluid-hover';

interface FluidMenuContextValue {
    registerItem: UseFluidHoverReturn['registerItem'];
    setActiveIndex: UseFluidHoverReturn['setActiveIndex'];
    claimIndex: () => number;
}

const FluidMenuContext = React.createContext<FluidMenuContextValue | null>(
    null
);

type SurfaceHandlerProps<T extends HTMLElement> = Pick<
    React.HTMLAttributes<T>,
    'onMouseEnter' | 'onMouseLeave' | 'onFocus'
>;

const isDisabledItem = (element: HTMLElement) =>
    element.hasAttribute('data-disabled');

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
    if (typeof ref === 'function') ref(value);
    else if (ref) (ref as React.MutableRefObject<T | null>).current = value;
}

function useMergedRef<T>(...refs: (React.Ref<T> | undefined)[]) {
    return React.useCallback(
        (value: T | null) => refs.forEach((ref) => assignRef(ref, value)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        refs
    );
}

function chain<E>(
    first: ((event: E) => void) | undefined,
    second: (event: E) => void
) {
    return (event: E) => {
        first?.(event);
        second(event);
    };
}

export function useFluidMenuSurface<T extends HTMLElement>(
    forwardedRef: React.Ref<T> | undefined,
    props: SurfaceHandlerProps<T>
) {
    const containerRef = React.useRef<T>(null);
    const hover = useFluidHover(containerRef, {
        gapClick: false,
        isItemDisabled: isDisabledItem
    });
    const ref = useMergedRef<T>(containerRef, forwardedRef);

    return {
        hover,
        surfaceProps: {
            ref,
            onMouseEnter: chain(
                props.onMouseEnter,
                hover.handlers.onMouseEnter
            ),
            onMouseLeave: chain(
                props.onMouseLeave,
                hover.handlers.onMouseLeave
            ),
            onFocus: chain(props.onFocus, (event: React.FocusEvent<T>) => {
                if (event.target === event.currentTarget) {
                    hover.setActiveIndex(null);
                }
            })
        }
    };
}

export function FluidMenuBody({
    hover,
    children
}: {
    hover: UseFluidHoverReturn;
    children: React.ReactNode;
}) {
    const nextIndex = React.useRef(0);
    const value = React.useMemo<FluidMenuContextValue>(
        () => ({
            registerItem: hover.registerItem,
            setActiveIndex: hover.setActiveIndex,
            claimIndex: () => nextIndex.current++
        }),
        [hover.registerItem, hover.setActiveIndex]
    );

    return (
        <FluidMenuContext.Provider value={value}>
            <FluidHoverHighlight
                hover={hover}
                className="rounded-lg in-has-[[data-fluid-hover-active][data-destructive]]:bg-destructive/10"
            />
            {children}
        </FluidMenuContext.Provider>
    );
}

export function useFluidMenuItem<T extends HTMLElement>(
    forwardedRef: React.Ref<T> | undefined,
    onFocus: React.FocusEventHandler<T> | undefined,
    highlight = true
) {
    const menu = React.useContext(FluidMenuContext);
    const itemRef = React.useRef<T>(null);
    const [index] = React.useState(() => menu?.claimIndex());
    useRegisterFluidHoverItem(
        highlight ? menu?.registerItem : undefined,
        index,
        itemRef
    );

    return {
        ref: useMergedRef<T>(itemRef, forwardedRef),
        onFocus: (event: React.FocusEvent<T>) => {
            onFocus?.(event);
            if (!menu || index === undefined) return;
            menu.setActiveIndex(highlight ? index : null);
        }
    };
}
